import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import type { SxProps } from '@mui/material/styles'

import {
  closestCenter,
  DndContext,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

import ButtonField from 'components_new/molecules/ButtonField'

import NameCell, { NameCellContext } from './NameCell'
import TreeDndTableRow from './TreeDndTableRow'
import {
  CellsComponentType,
  FlattenedNode,
  TreeColumn,
  TreeNode
} from './types'
import {
  anyNodeHasChildren,
  buildTree,
  countAllDescendants,
  flattenTree,
  getDescendants,
  getDragDepth,
  getProjection,
  hasChildren as nodeHasChildren
} from './utils'

const INDENT_SIZE = 24

interface Props {
  CellsComponent?: CellsComponentType
  columns: TreeColumn[]
  data: TreeNode[]
  disableDrag?: boolean
  editMode?: boolean
  expandedIds: string[]
  onExpandChange: (expandedIds: string[]) => void
  onRowAdd?: () => void
  onTreeChange: (value: TreeNode[]) => void
  renderDragOverlay?: (node: FlattenedNode) => ReactNode
  renderName?: (node: FlattenedNode) => ReactNode
  sx?: SxProps
}

const TreeDndTable = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    CellsComponent,
    columns,
    data,
    disableDrag,
    editMode,
    expandedIds: expandedIdsProp,
    onExpandChange,
    onRowAdd,
    onTreeChange,
    renderDragOverlay,
    renderName,
    sx
  } = props

  const { t } = useTranslation()

  const expandedIds = useMemo(() => new Set(expandedIdsProp), [expandedIdsProp])
  const [flattenedItems, setFlattenedItems] = useState<FlattenedNode[]>(() =>
    flattenTree(data, expandedIds)
  )
  const [activeId, setActiveId] = useState<string | null>(null)

  const flattenedItemsRef = useRef(flattenedItems)

  flattenedItemsRef.current = flattenedItems

  const initialDepthRef = useRef(0)
  const sourceChildrenRef = useRef<FlattenedNode[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Derived values

  const sortableIds = useMemo(
    () => flattenedItems.map((n) => n.id),
    [flattenedItems]
  )

  const tableHasChildren = useMemo(() => anyNodeHasChildren(data), [data])

  const activeNode = useMemo(
    () => flattenedItems.find((n) => n.id === activeId) || null,
    [activeId, flattenedItems]
  )

  // Sync flattenedItems when data or expandedIds change (but not during drag)
  const prevDataRef = useRef(data)
  const prevExpandedRef = useRef(expandedIds)

  if (
    !activeId &&
    (data !== prevDataRef.current || expandedIds !== prevExpandedRef.current)
  ) {
    prevDataRef.current = data
    prevExpandedRef.current = expandedIds
    setFlattenedItems(flattenTree(data, expandedIds))
  }

  // Handlers

  const handleToggleExpand = useCallback(
    (id: string) => {
      const next = new Set(expandedIds)

      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }

      onExpandChange(Array.from(next))
    },
    [expandedIds, onExpandChange]
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = event.active.id as string

    setActiveId(id)

    setFlattenedItems((items) => {
      const activeItem = items.find((item) => item.id === id)

      initialDepthRef.current = activeItem?.depth ?? 0
      sourceChildrenRef.current = []

      const descendants = getDescendants(items, id)

      return items.filter((item) => {
        if (descendants.has(item.id)) {
          sourceChildrenRef.current = [...sourceChildrenRef.current, item]

          return false
        }

        return true
      })
    })
  }, [])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    setFlattenedItems((items) => {
      const activeIndex = items.findIndex((i) => i.id === active.id)
      const overIndex = items.findIndex((i) => i.id === over.id)

      if (activeIndex === -1 || overIndex === -1) return items

      // Compute projection BEFORE the move (using over item's position)
      const offsetX = event.delta.x
      const dragDepth = getDragDepth(offsetX, INDENT_SIZE)
      const projectedDepth = initialDepthRef.current + dragDepth

      const { depth, parentId } = getProjection(
        items,
        over.id as string,
        projectedDepth
      )

      // Reorder
      const reordered = arrayMove(items, activeIndex, overIndex)

      // Apply projected depth/parentId to the dragged item
      return reordered.map((item) =>
        item.id === active.id ? { ...item, depth, parentId } : item
      )
    })
  }, [])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { active } = event

    setFlattenedItems((items) => {
      const activeIndex = items.findIndex((i) => i.id === active.id)

      if (activeIndex === -1) return items

      const offsetX = event.delta.x
      const dragDepth = getDragDepth(offsetX, INDENT_SIZE)
      const projectedDepth = initialDepthRef.current + dragDepth

      const { depth, parentId } = getProjection(
        items,
        active.id as string,
        projectedDepth
      )

      const currentItem = items[activeIndex]

      if (currentItem.depth === depth && currentItem.parentId === parentId) {
        return items
      }

      return items.map((item) =>
        item.id === active.id ? { ...item, depth, parentId } : item
      )
    })
  }, [])

  const handleDragEnd = useCallback(() => {
    setActiveId(null)

    const updatedTree = buildTree([
      ...flattenedItemsRef.current,
      ...sourceChildrenRef.current
    ])

    sourceChildrenRef.current = []

    setFlattenedItems(flattenTree(updatedTree, expandedIds))
    onTreeChange(updatedTree)
  }, [expandedIds, onTreeChange])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setFlattenedItems(flattenTree(data, expandedIds))
    sourceChildrenRef.current = []
  }, [data, expandedIds])

  const renderDefaultDragOverlay = (node: FlattenedNode) => {
    const descendantCount = countAllDescendants(node.id, data)
    const overlayContext = {
      anyRowHasChildren: anyNodeHasChildren(data),
      editMode,
      hasChildren: descendantCount > 0,
      node,
      onToggleExpand: handleToggleExpand
    }

    return (
      <NameCellContext.Provider value={overlayContext}>
        <Box
          sx={{
            position: 'relative'
          }}
        >
          {descendantCount > 0 ? (
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'primary.main',
                borderRadius: '50%',
                color: 'primary.contrastText',
                display: 'flex',
                fontSize: 12,
                fontWeight: 'bold',
                height: 20,
                justifyContent: 'center',
                position: 'absolute',
                left: -8,
                top: -8,
                width: 20,
                zIndex: 1
              }}
            >
              +{descendantCount}
            </Box>
          ) : null}
          <Table size="small">
            <TableBody>
              <TableRow
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 3,
                  opacity: 0.5
                }}
              >
                {CellsComponent ? (
                  <CellsComponent node={node} />
                ) : (
                  <>
                    <NameCell sx={{ width: columns[0]?.width }}>
                      {renderName ? (
                        renderName(node)
                      ) : (
                        <Text variant="body2">{node.data.name}</Text>
                      )}
                    </NameCell>
                    {columns.slice(1).map((column) => (
                      <TableCell
                        align={column.align}
                        key={column.key}
                        sx={{ width: column.width }}
                      >
                        {column.renderCell?.(node)}
                      </TableCell>
                    ))}
                  </>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </NameCellContext.Provider>
    )
  }

  // Render

  return (
    <TableContainer
      ref={ref}
      sx={{
        ...(editMode
          ? {
              borderRadius: 1,
              outline: '1px solid',
              outlineColor: 'primary.main',
              pt: 1
            }
          : null),
        ...sx
      }}
    >
      <DndContext
        collisionDetection={closestCenter}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  align={column.align}
                  key={column.key}
                  padding={column.padding}
                  sx={{ width: column.width }}
                  variant="head"
                >
                  {column.title ? (
                    <Text fontWeight="bold" variant="body2">
                      {column.title}
                    </Text>
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              {flattenedItems.map((node) => (
                <TreeDndTableRow
                  anyRowHasChildren={tableHasChildren}
                  CellsComponent={CellsComponent}
                  columns={columns}
                  disableDrag={disableDrag}
                  editMode={editMode}
                  hasChildren={nodeHasChildren(node.id, data)}
                  key={node.id}
                  node={node}
                  onToggleExpand={handleToggleExpand}
                  renderName={renderName}
                />
              ))}
            </SortableContext>

            {editMode && onRowAdd ? (
              <TableRow>
                <TableCell colSpan={columns.length} padding="none">
                  <ButtonField
                    disableBorder={true}
                    disableBorderRadius={true}
                    fullWidth={true}
                    onClick={onRowAdd}
                    orientation="horizontal"
                    title={t('global', 'add')}
                  />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <DragOverlay>
          {activeNode
            ? (renderDragOverlay || renderDefaultDragOverlay)(activeNode)
            : null}
        </DragOverlay>
      </DndContext>
    </TableContainer>
  )
})

TreeDndTable.displayName = 'TreeDndTable'
export default TreeDndTable
